import { ScheduleMessageModal } from '@/features/agendamentos/components/ScheduleMessageModal';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { useAuthStore } from '@/shared/stores/auth-store';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRightLeft,
  CalendarClock,
  Check,
  ChevronDown,
  Loader2,
  Lock,
  MessageCircleX,
  MessageSquare,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Search,
  Undo2,
  Upload,
  User,
  type Video,
} from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  acceptTicket,
  buscarNaConversa,
  closeTicket,
  editMessage,
  descartarPacote,
  enviarPacoteAgora,
  markTicketRead,
  queueTicket,
  reopenTicket,
  revokeMessage,
  segurarPacote,
  sendInternalNote,
  sendMediaMessage,
  sendMessage,
  soltarPacote,
} from '../api';
import type { EstadoDoPacote, ResultadoDaBusca } from '../api';
import type { MessagesPage } from '../api';
import type { ReopenEvent, TransferEvent } from '../api';
import { aplicaStatusDoRead } from '../aplica-status-do-read';
import { carimboDaPromocao, carimboDoNascimento, maisNovaDoCache } from '../carimbo-da-bolha';
import { motivoDaFalhaDeEdicao, podeEditarMensagem } from '../edicao-de-mensagem';
import { dentroDoBlocoCarregado, pisoDosBanners } from '../eventos-do-bloco-carregado';
import {
  esquecerAlvoDaBusca,
  marcarAlvoDaBusca,
  mediaPreviewForType,
  useTicket,
  useTicketMessages,
} from '../hooks';
import { invalidateServerTruthLists, patchTicket } from '../inbox-debug';
import { anelDoAvatar } from '../lib/anel-do-canal';
import { MAX_ANEXOS } from '../lib/anexos';
import { dayLabel } from '../lib/format';
import { isPendingContentHidden } from '../lib/pending-content';
import { TransferPerson } from '../lib/transfer-icon';
import {
  carimboDeOrdenacao,
  comPacotesAbertosNoFim,
  pacoteAberto,
  proximoDesprendimento,
} from '../pacote-aberto-no-fim';
import { agoraDoServidor } from '../relogio-do-servidor';
import { snapshotDaResposta } from '../snapshot-da-resposta';
import { getSocket } from '../socket';
import type { ChatMessage } from '../types';
import { useCallSession } from '../voip/call-session';
import { useVoipEnabled } from '../voip/useVoipCall';
import { BuscaNaConversa } from './BuscaNaConversa';
import { CloseModal } from './CloseModal';
import { Composer, type ComposerHandle } from './Composer';
import { ContactDetailsModal } from './ContactDetailsModal';
import { CopiarNumero } from './CopiarNumero';
import { MessageBubble } from './MessageBubble';
import { TagsPopover } from './TagsPopover';
import { TemplatePickerModal } from './TemplatePickerModal';
import { TransferModal } from './TransferModal';

interface Props {
  ticketId: number | null;
  /** Disparado após fechar o ticket — pai deselect pra voltar ao placeholder. */
  onTicketClosed?: () => void;
  /** Janela estreita (lista e conversa não cabem juntas): mostra a setinha
   *  de voltar pra lista no cabeçalho. Ausente = layout lado a lado. */
  onVoltar?: () => void;
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * Quantos blocos de histórico ficam guardados por conversa depois que o
 * atendente sai dela. Cada bloco = 20 mensagens, então 2 = as 40 mais recentes.
 */
const BLOCOS_GUARDADOS_AO_SAIR = 2;

/**
 * Pacotes que ESTA aba segurou (modo inteligente) e ainda não soltou — de
 * qualquer conversa, não só a aberta — com o instante em que segurou. Só serve
 * pro `pagehide`: F5 mata todos os rascunhos da aba de uma vez, então todos os
 * pacotes segurados por eles precisam ser soltos. Um pacote não vive mais que
 * o teto máximo configurável (600 s): o que passou disso já saiu e é podado,
 * pra uma aba aberta o dia inteiro não acumular ids nem disparar dezenas de
 * requisições no F5. Entrada de pacote que já saiu é inofensiva (409).
 */
const pacotesSeguradosNestaAba = new Map<number, number>();
const VIDA_MAXIMA_DE_UM_PACOTE_MS = 600_000;

function podaPacotesSegurados(): void {
  const agora = performance.now();
  for (const [id, em] of pacotesSeguradosNestaAba) {
    if (agora - em > VIDA_MAXIMA_DE_UM_PACOTE_MS) pacotesSeguradosNestaAba.delete(id);
  }
}

export function ThreadPanel({ ticketId, onTicketClosed, onVoltar }: Props) {
  const ticketQ = useTicket(ticketId);
  const userProfile = useAuthStore((s) => s.user?.profile);
  const userName = useAuthStore((s) => s.user?.name);
  const userId = useAuthStore((s) => s.user?.id);
  const isSupport = useAuthStore((s) => Boolean(s.user?.impersonating));
  // Fila cega: canal com hide_pending_content bloqueia a conversa de pendentes
  // pra não-admin até aceitar. NÃO busca as mensagens (servidor 403) — mostra
  // placeholder.
  const contentHidden = isPendingContentHidden(
    ticketQ.data?.whatsapp_number.hide_pending_content,
    ticketQ.data?.status ?? '',
    userProfile,
  );
  /**
   * SÓ busca mensagens DEPOIS de o cabeçalho chegar.
   *
   * Antes, a busca de mensagens e a do cabeçalho saíam JUNTAS. Como o
   * `contentHidden` depende do cabeçalho (a flag do canal + o status vêm de
   * `ticketQ`), enquanto ele não chegava o painel assumia "liberado" e já
   * pedia as mensagens — que voltavam 403 na fila cega, porque o servidor SÓ
   * então via que era pendente + oculto. Corrida: o 403 saía antes de o
   * cabeçalho dizer "essa é trancada". Reproduzido no Network: 3x
   * `/messages` 403.
   *
   * Esperar o cabeçalho (`ticketQ.data` presente) elimina a corrida: com ele
   * em mãos, o painel sabe se pode ver ANTES de perguntar. Bloqueada → nunca
   * pede, mostra "aceite para ver". Liberada → busca normal. Sem custo real:
   * o cabeçalho já ia ser buscado de qualquer forma; só a ORDEM mudou.
   */
  const cabecalhoChegou = ticketQ.data !== undefined;
  const messagesQ = useTicketMessages(ticketId, cabecalhoChegou && !contentHidden);
  const qc = useQueryClient();
  const [closeOpen, setCloseOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  // VoIP (Fase 2): chamada de voz — só canal Qr Code API V2 (zapo). A
  // ligação vive na sessão global (AppShell), não aqui: segue no ar ao trocar
  // de conversa/página e não trava o painel (janelinha flutuante).
  const callSession = useCallSession();
  const [agendarAberto, setAgendarAberto] = useState(false);
  const voipEnabled = useVoipEnabled();
  const [contactDetailsOpen, setContactDetailsOpen] = useState(false);
  // Mensagem sendo respondida (citação no Composer). Limpa após envio,
  // X manual no banner, OU ao trocar de conversa (não tem sentido manter
  // citação de uma conversa em outra). Apenas msgs com `wamid` podem ser
  // respondidas — o contexto WhatsApp/Meta exige o wamid pra criar a citação.
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  // Mensagem sendo EDITADA (faixa "Editando" no Composer). Exclusiva com a
  // citação: entrar num modo sai do outro.
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  // Edições que ESTE painel pediu e ainda não voltaram — só elas viram toast
  // de falha (outro atendente olhando a conversa não precisa do aviso).
  const editsPendentesRef = useRef<Set<number>>(new Set());
  // Reset ao trocar de ticket — evita banner "respondendo a..." persistir
  // quando user navega pra outra conversa sem ter enviado.
  useEffect(() => {
    setReplyingTo(null);
    setEditingMessage(null);
  }, [ticketId]);

  // Busca na conversa. O texto digitado vive na barra (BuscaNaConversa); aqui
  // chega só o termo depois da espera da digitação, os resultados (do mais
  // recente pro mais antigo) e qual deles está na tela.
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [termoDaBusca, setTermoDaBusca] = useState('');
  const [resultadosDaBusca, setResultadosDaBusca] = useState<ResultadoDaBusca[] | null>(null);
  const [buscaTruncada, setBuscaTruncada] = useState(false);
  const [indiceDaBusca, setIndiceDaBusca] = useState(0);
  const [buscando, setBuscando] = useState(false);
  const [descendo, setDescendo] = useState(false);
  const [focoDaBusca, setFocoDaBusca] = useState<number | null>(null);
  // Cada busca/salto novo invalida o anterior ainda em voo (digitou de novo,
  // clicou ↑ várias vezes): só o último mexe na tela.
  const seqDaBuscaRef = useRef(0);
  const seqDoSaltoRef = useRef(0);
  const fecharBusca = useCallback(() => {
    seqDaBuscaRef.current += 1;
    seqDoSaltoRef.current += 1;
    setBuscaAberta(false);
    setTermoDaBusca('');
    setResultadosDaBusca(null);
    setBuscaTruncada(false);
    setIndiceDaBusca(0);
    setBuscando(false);
    setDescendo(false);
    setFocoDaBusca(null);
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: fecha ao trocar de conversa
  useEffect(() => {
    fecharBusca();
  }, [ticketId, fecharBusca]);

  /**
   * Ao SAIR da conversa, descarta o histórico rolado e guarda só os blocos mais
   * recentes — o comportamento do WhatsApp: você volta no fim e rola de novo se
   * quiser o antigo.
   *
   * O motivo: a conversa é paginada, e quando ela é rebuscada o TanStack
   * rebusca TODAS as páginas carregadas, uma a uma. Um atendimento em que o
   * atendente rolou 18 blocos custava 18 requisições em cadeia a cada rebusca —
   * medido no rastro, ao fechar um atendimento. Guardando 2, custa 2.
   *
   * Por que aqui e não com `maxPages` do próprio TanStack, que existe pra isso:
   *   1. `maxPages` exige navegação nos DOIS sentidos, e o servidor só aceita
   *      `before` (mais antigas) — não há como buscar de volta uma página
   *      recente descartada;
   *   2. pior, ele descarta a página MAIS RECENTE quando se rola pra cima — e é
   *      exatamente nela que o tempo real insere a mensagem que chega.
   *
   * Descartando só do lado antigo, nenhum dos dois problemas existe. E o ganho
   * de abrir a conversa sem gastar requisição continua de pé, porque o bloco
   * recente permanece em cache.
   */
  useEffect(() => {
    if (ticketId === null) return;
    return () => {
      // O alvo da busca faz a rebusca descer até a mensagem achada — sem o
      // histórico rolado (descartado abaixo), não tem mais por que descer.
      esquecerAlvoDaBusca(ticketId);
      qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
        ['ticket-messages', ticketId],
        (prev) => {
          if (!prev || prev.pages.length <= BLOCOS_GUARDADOS_AO_SAIR) return prev;
          // `pages[0]` é o bloco mais NOVO (vide o comentário de
          // `useTicketMessages`), então cortar do fim descarta o histórico
          // antigo. `pageParams` tem que ser cortado junto — o TanStack exige os
          // dois alinhados, e o último param preservado é o que permite rolar
          // pra cima e buscar o resto de novo.
          return {
            pages: prev.pages.slice(0, BLOCOS_GUARDADOS_AO_SAIR),
            pageParams: prev.pageParams.slice(0, BLOCOS_GUARDADOS_AO_SAIR),
          };
        },
      );
    };
  }, [ticketId, qc]);
  // Ref pra área das tags no header — usada pra distinguir click no +Tag/tag
  // pills (TagsPopover trata) vs click no resto da região (abre modal de info).
  const tagsRegionRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Wrapper único de TODO o conteúdo do scroll. O ResizeObserver observa esse
  // wrapper pra detectar qualquer crescimento interno (mídia em qualquer bolha).
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  // Sentinela 1px no fim da lista. `scrollIntoView` nele recalcula a posição
  // dinamicamente conforme mídia cresce — não precisa rastrear scrollHeight.
  const endSentinelRef = useRef<HTMLDivElement>(null);

  // Optimistic update + Composer síncrono + gate 503 (sessão 2026-05-06):
  //
  // - onMutate insere bolha temp (tempId negativo) no cache → bolha INSTANTÂNEA
  // - Composer aguarda mutation.mutateAsync (síncrono) — input só limpa em sucesso
  // - 503 SystemDegradedError → onError marca bolha como ❌ vermelho + toast
  //   + Composer NÃO limpa input (text preservado pra retry)
  // - Sucesso → socket emit message.received chega ~50-200ms depois e dedup
  //   por message_id remove a bolha temp e insere a real (com id positivo)
  //
  // Por que mantemos optimistic E sync: optimistic dá feedback instantâneo
  // (UX boa); sync garante que o erro 503 fica visível (botão spinner para,
  // toast aparece, input preserva texto). Removendo optimistic, a bolha
  // dependeria 100% do socket emit chegar — frágil contra reconnect/recreate.
  type CtxOptimistic = { tempId?: number };

  const insertOptimistic = (body: string, extra: Partial<ChatMessage> = {}): number => {
    const tempId = -Date.now();
    // Piso do carimbo (vide carimbo-da-bolha.ts): a bolha nasce em
    // `max(agora, msg mais nova + 1ms)` — com o relógio do PC atrasado, quem
    // acabou de enviar ainda fica no FIM da conversa, nunca acima do cliente.
    const cachePre = qc.getQueryData<InfiniteData<MessagesPage, number | undefined>>([
      'ticket-messages',
      ticketId,
    ]);
    const agoraIso = new Date().toISOString();
    const carimbo = carimboDoNascimento(
      agoraIso,
      cachePre ? (maisNovaDoCache(cachePre.pages)?.created_at ?? null) : null,
    );
    qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
      ['ticket-messages', ticketId],
      (prev) => {
        if (!prev || prev.pages.length === 0) return prev;
        const optimistic: ChatMessage = {
          id: tempId,
          wamid: null,
          direction: 'outbound',
          type: 'text',
          body,
          media_id: null,
          media_mime: null,
          media_status: null,
          media_waveform: null,
          media_duration_ms: null,
          media_width: null,
          media_height: null,
          media_original_name: null,
          status_rank: 0,
          sent_at: null,
          delivered_at: null,
          read_at: null,
          failed_at: null,
          error_title: null,
          created_at: carimbo,
          sent_by_user_id: null,
          ...extra,
        };
        const [firstPage, ...rest] = prev.pages;
        if (!firstPage) return prev;
        return {
          ...prev,
          pages: [{ ...firstPage, messages: [...firstPage.messages, optimistic] }, ...rest],
        };
      },
    );
    return tempId;
  };

  const handleSendError = (err: unknown, _vars: unknown, ctx?: CtxOptimistic): void => {
    if (err instanceof ApiError) {
      const detail = err.problem.detail;
      toast.error(err.problem.title, detail ? { description: detail } : undefined);
    } else {
      toast.error('Falha ao enviar mensagem', {
        description: 'Verifique sua conexão e tente novamente.',
      });
    }
    // Em SYSTEM_DEGRADED 503, a API insere a row no DB ANTES de lançar e
    // expõe message_id no context.messageId. Linkamos a bolha optimistic
    // (id negativo) ao id real ANTES de marcar FAILED — assim a bolha
    // sobrevive ao próximo refetch do cache (DB tem a row).
    const realId =
      err instanceof ApiError && typeof err.problem.context?.messageId === 'number'
        ? (err.problem.context.messageId as number)
        : undefined;
    if (realId !== undefined && ctx?.tempId !== undefined) {
      linkTempToReal(ctx.tempId, realId);
    }
    // Marca bolha como FAILED — busca por realId (se linkado) ou tempId.
    const idToMark = realId ?? ctx?.tempId;
    if (idToMark !== undefined) {
      qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
        ['ticket-messages', ticketId],
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            pages: prev.pages.map((p) => ({
              ...p,
              messages: p.messages.map((m) =>
                m.id === idToMark
                  ? {
                      ...m,
                      status_rank: 99,
                      failed_at: new Date().toISOString(),
                      error_title:
                        err instanceof ApiError
                          ? (err.problem.title ?? 'Falha ao enviar')
                          : 'Falha ao enviar',
                    }
                  : m,
              ),
            })),
          };
        },
      );
    }
  };

  /**
   * Linka a bolha optimistic (id negativo via tempId) com o message_id REAL
   * que a API retornou no 202. Sem isso, eventos `message.status.updated`
   * subsequentes (ex: PUBLISH_FAILED 30s depois quando RabbitMQ off) não
   * conseguem achar a bolha pra marcar como ❌ — listener busca por id
   * positivo, bolha tem id negativo, sem match.
   */
  /**
   * Linka tempId → realId. Mas se o socket emit `message.received` JÁ inseriu
   * uma bolha com realId (race condition: socket chegou antes do onSuccess),
   * REMOVE a optimistic em vez de duplicar (caso contrário ficaríamos com 2
   * bolhas com mesmo id, React renderiza ambas).
   */
  const linkTempToReal = (
    tempId: number | undefined,
    realId: number,
    serverCreatedAt?: string,
  ): void => {
    if (tempId === undefined) return;
    qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
      ['ticket-messages', ticketId],
      (prev) => {
        if (!prev) return prev;
        // Já existe bolha com realId? (socket emit ganhou a corrida)
        const realExists = prev.pages.some((p) => p.messages.some((m) => m.id === realId));
        // Âncora do piso da promoção: a msg mais nova EXCLUINDO a própria
        // bolha (temp e real) — ela não pode ancorar a si mesma.
        const maisNova = maisNovaDoCache(prev.pages, new Set([tempId, realId]))?.created_at ?? null;
        return {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            messages: p.messages.flatMap((m) => {
              // Eco ganhou: a temp será dropada abaixo, mas a bolha REAL (que o
              // eco criou/promoveu) adota o carimbo do servidor aqui — era o
              // furo medido em 2026-07-24: nenhum dos dois vencedores da corrida
              // aplicava o carimbo, e o relógio da aba ficava pra sempre.
              if (realExists && m.id === realId) {
                return [
                  { ...m, created_at: carimboDaPromocao(m.created_at, serverCreatedAt, maisNova) },
                ];
              }
              if (m.id !== tempId) return [m];
              // Se realId já existe → DROPA a temp. Senão → promove tempId →
              // realId adotando o carimbo do servidor com piso (aba adiantada
              // volta do futuro; servidor atrás da última msg não inverte).
              return realExists
                ? []
                : [
                    {
                      ...m,
                      id: realId,
                      created_at: carimboDaPromocao(m.created_at, serverCreatedAt, maisNova),
                    },
                  ];
            }),
          })),
        };
      },
    );
  };

  /**
   * Motor (Fase 2) — pós-envio: remenda o card da lista LOCALMENTE (preview
   * novo, sobe pro topo, zera não-lido) em vez de rebuscar as 3 listas a cada
   * mensagem enviada (medido no srv01: era o custo escondido de 1.768
   * envios/hora). É a ação do PRÓPRIO atendente — sem juízo de visibilidade a
   * fazer. Visões server-truth (admin 'todos', busca, triagem) rebuscam.
   */
  const patchCardAfterSend = (preview: string | null): void => {
    if (ticketId === null) return;
    patchTicket(
      qc,
      ticketId,
      (t) => ({
        ...t,
        ...(preview !== null && { last_message_preview: preview }),
        // Hora do servidor, não a da aba: o card compara o aviso de
        // fechamento do pacote (carimbo do servidor) com esta hora pra não
        // voltar ao texto antigo — com o relógio do PC torto, a comparação
        // errava. (Não serve o `created_at` da resposta: texto que entra num
        // pacote aberto devolve o da âncora, segundos atrás.)
        last_message_at: new Date(agoraDoServidor()).toISOString(),
        unread_count: 0,
      }),
      { origin: 'patchCardAfterSend', event: 'send', reorder: true },
    );
    invalidateServerTruthLists(qc, 'patchCardAfterSend');
  };

  const sendMut = useMutation({
    mutationFn: (input: { body: string; replyToWamid?: string | undefined }) =>
      sendMessage({
        ticket_id: ticketId as number,
        body: input.body,
        ...(input.replyToWamid ? { reply_to_wamid: input.replyToWamid } : {}),
      }),
    onMutate: (input): CtxOptimistic => ({
      // A bolha otimista da RESPOSTA nasce COM a citação — montada do
      // `replyingTo` que o painel já tem em mãos. Antes ela saía sem `reply_to`
      // e a citação só aparecia se o eco do servidor chegasse e completasse a
      // bolha; se o eco não viesse (fora da sala), ficava marcada "só pro
      // cliente". O snapshot local nao depende de eco nenhum.
      tempId: insertOptimistic(input.body, snapshotDaResposta(replyingTo)),
    }),
    onSuccess: (data, vars, ctx) => {
      linkTempToReal(ctx?.tempId, data.message_id, data.created_at);
      // Motor (Fase 2): enviar msg não rebusca as 3 listas — remenda o card
      // local (preview + topo + zera não-lido). Verdade do servidor chega em
      // qualquer evento seguinte; visões filtradas/'todos' rebuscam.
      patchCardAfterSend(vars.body);
      setReplyingTo(null);
    },
    onError: handleSendError,
  });

  const sendMediaMut = useMutation({
    mutationFn: (input: {
      type: 'image' | 'audio' | 'video' | 'document';
      media_id: number;
      caption?: string;
      replyToWamid?: string | undefined;
      width?: number;
      height?: number;
      poster?: string | null;
    }) =>
      sendMediaMessage({
        ticket_id: ticketId as number,
        type: input.type,
        media_id: input.media_id,
        ...(input.caption ? { caption: input.caption } : {}),
        ...(input.replyToWamid ? { reply_to_wamid: input.replyToWamid } : {}),
      }),
    onMutate: (input): CtxOptimistic => ({
      tempId: insertOptimistic(input.caption ?? '', {
        type: input.type,
        media_id: input.media_id,
        media_status: 'stored',
        // Resposta com mídia: mesma citação otimista do texto (vide sendMut).
        ...snapshotDaResposta(replyingTo),
        ...(input.width &&
          input.height && {
            media_width: input.width,
            media_height: input.height,
          }),
        // Poster dataURL client-side → bolha de vídeo nasce com preview,
        // sem flash de "preto → thumb". Substituído pela URL HMAC real
        // (ffmpeg server-side) no próximo refetch.
        ...(input.poster && { media_thumbnail_url: input.poster }),
      }),
    }),
    onSuccess: (data, vars, ctx) => {
      linkTempToReal(ctx?.tempId, data.message_id, data.created_at);
      patchCardAfterSend(vars.caption ?? mediaPreviewForType(vars.type));
      setReplyingTo(null);
    },
    onError: handleSendError,
  });

  // Nota interna — fica só no painel (não vai pro cliente). Bolha optimistic
  // nasce já amarela (internal_note=true) com o nome do autor pra render na hora.
  const sendNoteMut = useMutation({
    mutationFn: (text: string) => sendInternalNote(ticketId as number, text),
    onMutate: (text): CtxOptimistic => ({
      tempId: insertOptimistic(text, {
        internal_note: true,
        sent_by_user_id: userId ?? null,
        sent_by_user_name: userName ?? null,
      }),
    }),
    onSuccess: (data, vars, ctx) => {
      linkTempToReal(ctx?.tempId, data.message_id, data.created_at);
      patchCardAfterSend(vars);
    },
    onError: handleSendError,
  });

  /**
   * Ação recusada pelo servidor (aceitar, fila, fechar). 409 = o atendimento
   * mudou nesse meio-tempo — outro aceitou, fechou ou o cliente já está com
   * outro agente: o cabeçalho ainda mostra o estado velho e, sem rebuscar, o
   * botão ficava ali e cada clique gerava outra recusa.
   */
  const avisaRecusa = (err: unknown, origem: string): void => {
    if (!(err instanceof ApiError)) return;
    toast.error(err.problem.title, { description: err.problem.detail });
    if (err.status === 409) {
      void qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      invalidateServerTruthLists(qc, origem);
    }
  };

  const acceptMut = useMutation({
    mutationFn: () => acceptTicket(ticketId as number),
    onSuccess: () => {
      // Motor (Fase 2): a lista é atualizada pelo EVENTO com cartão que o
      // servidor emite (o autor também o recebe). Aqui só as visões que
      // precisam da verdade do servidor (admin 'todos', busca, triagem).
      invalidateServerTruthLists(qc, 'threadAcceptMut');
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] });
      // Auto-focus rola via useEffect abaixo que observa `ticket?.status` —
      // quando refetch retornar e status virar 'active', o composer (que
      // estava disabled em 'pending') ficará habilitado E o effect dispara
      // o focusInput. Tentar focar aqui falha porque textarea[disabled]
      // ignora .focus() do browser.
    },
    // Ex.: limite de atendimentos atingido, ou outro atendente aceitou antes.
    onError: (err) => avisaRecusa(err, 'threadAcceptMutRecusado'),
  });

  const queueMut = useMutation({
    mutationFn: () => queueTicket(ticketId as number),
    onSuccess: () => {
      invalidateServerTruthLists(qc, 'threadQueueMut');
      // Mesmo caso do fechar: a conversa sai da tela logo abaixo, então marca
      // como velho sem buscar. Vide o comentário longo em `closeMut`.
      qc.invalidateQueries({ queryKey: ['ticket', ticketId], refetchType: 'none' });
      qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId], refetchType: 'none' });
      // Saiu do controle do atendente (voltou pra fila) — fecha a thread.
      onTicketClosed?.();
    },
    onError: (err) => avisaRecusa(err, 'threadQueueMutRecusado'),
  });

  const closeMut = useMutation({
    mutationFn: ({ reason, notes }: { reason?: number | undefined; notes?: string }) =>
      closeTicket(ticketId as number, {
        ...(reason !== undefined && { closing_reason_id: reason }),
        ...(notes && { notes }),
      }),
    onSuccess: () => {
      invalidateServerTruthLists(qc, 'threadCloseMut');
      // `refetchType: 'none'` = marca como velho SEM buscar agora. A conversa
      // está saindo da tela na linha de baixo — buscar cabeçalho e mensagens
      // de algo que ninguém vai olhar era jogar fora (2 requisições por
      // fechamento, medidas no rastro). Quando o atendente reabrir, aí sim
      // busca, porque está marcada como velha.
      //
      // Não adianta trocar a ordem e invalidar depois do `onTicketClosed`: o
      // React AGENDA a saída da tela, não desmonta na hora — quando estas
      // linhas rodassem, a conversa ainda estaria montada e buscaria igual.
      // A intenção precisa ser explícita, e é isto que `'none'` diz.
      qc.invalidateQueries({ queryKey: ['ticket', ticketId], refetchType: 'none' });
      qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId], refetchType: 'none' });
      // Conversa "some" da tela — volta pro placeholder "Selecione um atendimento".
      onTicketClosed?.();
    },
    onError: (err) => avisaRecusa(err, 'threadCloseMutRecusado'),
  });

  const revokeMut = useMutation({
    mutationFn: (messageId: number) => revokeMessage(messageId),
    onSuccess: () => {
      // Realtime chega via 'message.deleted': atualiza a thread de todos e o
      // handler global invalida as listas (preview pode ter mudado). Aqui só
      // a thread própria + visões server-truth.
      qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] });
      invalidateServerTruthLists(qc, 'revokeMut');
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, {
          description: err.problem.detail ?? undefined,
        });
      } else {
        toast.error('Não foi possível excluir a mensagem');
      }
    },
  });

  // Edição: a API só enfileira. O texto novo chega por 'message.edited'
  // (handler global já atualiza a bolha); a falha chega por
  // 'message.edit.failed' — escutados abaixo.
  const editMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => editMessage(id, body),
    onSuccess: (res, vars) => {
      if (res.status !== 'pending') editsPendentesRef.current.delete(vars.id);
    },
    onError: (err: unknown, vars) => {
      editsPendentesRef.current.delete(vars.id);
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail ?? undefined });
      } else {
        toast.error('Não foi possível editar a mensagem');
      }
    },
  });

  useEffect(() => {
    const socket = getSocket();
    const onEdited = (evt: { message_id: number }): void => {
      editsPendentesRef.current.delete(evt.message_id);
    };
    const onEditFailed = (evt: { message_id: number; reason?: string }): void => {
      if (!editsPendentesRef.current.delete(evt.message_id)) return;
      toast.error('Não foi possível editar a mensagem', {
        description: motivoDaFalhaDeEdicao(evt.reason),
      });
    };
    socket.on('message.edited', onEdited);
    socket.on('message.edit.failed', onEditFailed);
    return () => {
      socket.off('message.edited', onEdited);
      socket.off('message.edit.failed', onEditFailed);
    };
  }, []);

  const handleEditMessage = (m: ChatMessage): void => {
    setReplyingTo(null);
    setEditingMessage(m);
  };

  const handleSaveEdit = (body: string): void => {
    const m = editingMessage;
    setEditingMessage(null);
    if (!m || m.id <= 0) return;
    editsPendentesRef.current.add(m.id);
    editMut.mutate({ id: m.id, body });
  };

  /**
   * Fecha o pacote de texto na hora (clique na contagem ao lado do balão).
   *
   * Sem confirmação de propósito: a ação só ANTECIPA o que ia acontecer
   * sozinho em segundos — pedir "tem certeza?" pra isso seria atrito puro. O
   * servidor é idempotente, então clique repetido não duplica o envio.
   *
   * A bolha se atualiza pelo realtime (o fechamento reemite a mensagem, agora
   * sem prazo), então aqui não há nada a fazer no sucesso.
   */
  const enviarAgoraMut = useMutation({
    mutationFn: (messageId: number) => enviarPacoteAgora(messageId),
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        // "Já foi enviada" é corrida com o próprio cronômetro, não erro de
        // verdade: o texto saiu, que era o que o atendente queria.
        if (err.problem.title?.includes('já foi enviada')) return;
        toast.error(err.problem.title, { description: err.problem.detail ?? undefined });
      } else {
        toast.error('Não foi possível enviar agora');
      }
    },
  });

  const handleEnviarAgora = (m: ChatMessage): void => {
    if (!m.id || m.id < 0) return;
    enviarAgoraMut.mutate(m.id);
  };

  /**
   * Descarta o pacote de texto que ainda não saiu (menu do balão). Sem
   * confirmação (decisão do dono): o texto não se perde — volta pro campo de
   * digitar. A bolha sai da conversa assim que o servidor confirma; as outras
   * abas recebem `message.discarded` e fazem o mesmo.
   */
  const descartarMut = useMutation({
    mutationFn: (messageId: number) => descartarPacote(messageId),
    onSuccess: (r) => {
      removerMensagemDoCache(r.message_id);
      composerRef.current?.devolverTexto(r.body);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail ?? undefined });
      } else {
        toast.error('Não foi possível descartar a mensagem');
      }
    },
  });

  const removerMensagemDoCache = (messageId: number): void => {
    if (ticketId == null) return;
    qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
      ['ticket-messages', ticketId],
      (prev) =>
        prev && {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            messages: p.messages.filter((x) => x.id !== messageId),
          })),
        },
    );
  };

  const handleDescartar = (m: ChatMessage): void => {
    if (!m.id || m.id < 0 || descartarMut.isPending) return;
    descartarMut.mutate(m.id);
  };

  const handleDeleteMessage = async (m: ChatMessage): Promise<void> => {
    if (!m.id) return;
    const ok = await confirm({
      title: 'Excluir mensagem?',
      description:
        'A mensagem será apagada também no WhatsApp do contato. Não é possível desfazer.',
      confirmText: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    revokeMut.mutate(m.id);
  };

  // Jump to msg citada (clique no QuotedPreview). Localiza a bolha alvo via
  // data-wamid no DOM, scrollIntoView smooth e pulsa o destaque ~1.5s.
  //
  // Se a msg citada NÃO está nas páginas carregadas, o handler dispara
  // `fetchNextPage()` em loop (até 50 páginas = ~1000 msgs) procurando o
  // wamid no DOM após cada batch. Toast de loading aparece durante a busca;
  // se esgotar sem achar (msg deletada ou muito antiga), toast de erro.
  // Guard `jumpingRef` previne re-entry se user clica 2x rápido.
  const jumpingRef = useRef(false);
  const handleJumpToQuoted = useCallback(
    async (wamid: string): Promise<void> => {
      const root = scrollRef.current;
      if (!root) return;
      const escaped = CSS.escape(wamid);

      const findAndPulse = (): boolean => {
        const target = root.querySelector<HTMLElement>(`[data-wamid="${escaped}"]`);
        if (!target) return false;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('!bg-brand-50', 'ring-2', 'ring-brand-200', 'rounded-md');
        window.setTimeout(() => {
          target.classList.remove('!bg-brand-50', 'ring-2', 'ring-brand-200', 'rounded-md');
        }, 1500);
        return true;
      };

      // Já tá no DOM? Scroll direto.
      if (findAndPulse()) return;

      if (jumpingRef.current) return; // já tem busca em andamento
      jumpingRef.current = true;
      const toastId = toast.loading('Procurando mensagem…');

      try {
        let safety = 50; // cap = ~50 × 20 msgs = 1000 msgs back
        let hasMore = messagesQ.hasNextPage ?? false;
        while (hasMore && safety > 0) {
          const result = await messagesQ.fetchNextPage();
          // Tick pro React renderizar as bolhas novas no DOM antes do query.
          await new Promise((r) => setTimeout(r, 50));
          if (findAndPulse()) {
            toast.dismiss(toastId);
            return;
          }
          hasMore = result.hasNextPage ?? false;
          safety -= 1;
        }
        toast.dismiss(toastId);
        toast.info('Mensagem não encontrada', {
          description:
            'A mensagem citada pode ter sido apagada ou está fora do alcance do histórico.',
        });
      } finally {
        jumpingRef.current = false;
      }
    },
    [messagesQ],
  );

  const reopenMut = useMutation({
    mutationFn: () => reopenTicket(ticketId as number),
    onSuccess: () => {
      invalidateServerTruthLists(qc, 'threadReopenMut');
      /**
       * Reabrir custava SEIS requisições — o dono flagrou no rastro. Metade era
       * duplicata pura: esta mutation invalidava, e o aviso `ticket.reopened`
       * invalidava de novo, então cada cache era buscado DUAS vezes.
       *
       * A CONVERSA sai daqui: quem a rebusca é o aviso `ticket.reopened` no
       * socket (que agora precisa dela — reabrir acrescenta a faixa "Atendimento
       * reaberto por X" na thread). Invalidar aqui TAMBÉM recriaria a dupla
       * busca que este comentário existe pra impedir.
       *
       * O CABEÇALHO fica, e de propósito — diferente de fechar e enfileirar
       * (que usam `refetchType: 'none'` porque a conversa SAI da tela), aqui ela
       * CONTINUA na frente do atendente: o botão Reabrir tem que sumir e o
       * composer tem que destravar na hora. Se o aviso do socket não chegar, ele
       * clicaria de novo achando que não funcionou. Uma requisição é o preço de
       * não deixar a tela mentindo sobre uma ação que ele acabou de fazer.
       *
       * (O aviso do socket também corrige o cabeçalho, escrevendo a partir do
       * cartão — sem requisição. Esta aqui é a rede de segurança pro caso de ele
       * não chegar.)
       */
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
    },
    onError: (err: unknown) => {
      // CONTACT_HAS_ACTIVE_TICKET é o caso "já tem atendimento aberto desse contato".
      // Outros erros caem no fallback genérico.
      if (err instanceof ApiError) {
        const detail = err.problem.detail;
        toast.error(err.problem.title, detail ? { description: detail } : undefined);
      } else {
        toast.error('Não foi possível reabrir o atendimento', {
          description: 'Tente novamente em alguns segundos.',
        });
      }
    },
  });

  // Flatten das páginas do infinite query: pages[0] = batch mais novo (sem cursor),
  // pages[N] = batches anteriores. Concat na ordem reversa pra cronologia oldest→newest.
  const allMessages = useMemo(() => {
    if (!messagesQ.data) return [];
    return [...messagesQ.data.pages].reverse().flatMap((p) => p.messages);
  }, [messagesQ.data]);

  /**
   * Agrupador no modo INTELIGENTE: enquanto a caixa tem texto, o relógio do
   * pacote aberto fica parado no que faltava; esvaziou, retoma dali.
   *
   * Uma chamada por TRANSIÇÃO (vazio→texto = segurar, texto→vazio = soltar),
   * nunca periódica. O pacote aberto é a última mensagem minha ainda com prazo;
   * se ele fechar sozinho (teto, "enviar agora"), o realtime zera o prazo, a
   * âncora some daqui e o estado local se esquece — sem chamada nenhuma.
   * Enviar destrava pelo próprio envio (o texto entra e o relógio reinicia);
   * o "soltar" que a caixa vazia dispara depois vira no-op no servidor.
   */
  const composerTemTextoRef = useRef(false);
  const seguradoRef = useRef<number | null>(null);
  const modoInteligente = ticketQ.data?.whatsapp_number.text_batch_mode === 'smart';
  const ancoraAberta = useMemo(() => {
    if (!modoInteligente) return null;
    const agora = agoraDoServidor();
    for (let i = allMessages.length - 1; i >= 0; i--) {
      const m = allMessages[i]!;
      if (
        m.direction === 'outbound' &&
        m.id > 0 &&
        m.batch_open_until &&
        new Date(m.batch_open_until).getTime() > agora
      ) {
        return m.id;
      }
    }
    return null;
  }, [allMessages, modoInteligente]);

  const aplicaEstadoDoPacote = useCallback(
    (messageId: number, estado: EstadoDoPacote): void => {
      qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
        ['ticket-messages', ticketId],
        (prev) =>
          prev && {
            ...prev,
            pages: prev.pages.map((p) => ({
              ...p,
              messages: p.messages.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      batch_open_until: estado.batch_open_until,
                      batch_held_remaining_ms: estado.batch_held_remaining_ms,
                    }
                  : m,
              ),
            })),
          },
      );
    },
    [qc, ticketId],
  );

  const segurarOuSoltar = useCallback(
    (messageId: number, segurar: boolean): void => {
      const chamada = segurar ? segurarPacote : soltarPacote;
      seguradoRef.current = segurar ? messageId : null;
      if (segurar) {
        podaPacotesSegurados();
        pacotesSeguradosNestaAba.set(messageId, performance.now());
      } else {
        pacotesSeguradosNestaAba.delete(messageId);
      }
      chamada(messageId)
        .then((estado) => aplicaEstadoDoPacote(messageId, estado))
        .catch(() => {
          // Pacote já saiu (409) ou rede: o realtime/refetch corrige a bolha;
          // não há o que avisar — o texto do atendente continua na caixa.
        });
    },
    [aplicaEstadoDoPacote],
  );

  const onTextPresenceChange = useCallback(
    (hasText: boolean): void => {
      composerTemTextoRef.current = hasText;
      if (!modoInteligente) return;
      if (hasText) {
        if (ancoraAberta && seguradoRef.current !== ancoraAberta)
          segurarOuSoltar(ancoraAberta, true);
      } else if (seguradoRef.current !== null) {
        segurarOuSoltar(seguradoRef.current, false);
      }
    },
    [modoInteligente, ancoraAberta, segurarOuSoltar],
  );

  // A caixa já tinha texto quando um pacote NOVO abriu (ex.: enviou, o relógio
  // reiniciou, e o atendente já começou a próxima linha): segura o novo.
  // Âncora sumiu (fechou): esquece o que estava segurado.
  useEffect(() => {
    if (!modoInteligente) return;
    if (ancoraAberta === null) {
      seguradoRef.current = null;
      return;
    }
    if (composerTemTextoRef.current && seguradoRef.current !== ancoraAberta) {
      segurarOuSoltar(ancoraAberta, true);
    }
  }, [ancoraAberta, modoInteligente, segurarOuSoltar]);

  // Trocar de conversa NÃO solta (decisão do dono, 20/set): começou a
  // digitar, foi consultar outra conversa ou outro sistema e volta pra
  // terminar — é uso normal do modo inteligente, e soltar mandaria a 1ª linha
  // sozinha no meio da consulta. O pacote fica segurado (rascunho guardado =
  // texto na caixa) até a caixa esvaziar ou o teto do canal vencer — o mesmo
  // que acontece ao ir pra outra janela. Voltando com o rascunho, o
  // compositor avisa "tem texto" ao montar e re-segura (idempotente).
  //
  // F5 / fechar aba / sair do site é diferente: o rascunho (só memória) morre
  // junto, então não há mais o que agrupar — segurar até o teto só atrasaria
  // o cliente. A página some antes de qualquer cleanup do React rodar;
  // `pagehide` é o último aviso do navegador e, com `keepalive`, ele termina
  // de enviar o "soltar" mesmo sem a página. Solta TODOS os segurados desta
  // aba (inclusive de conversas deixadas pra trás); id de pacote que já saiu
  // responde 409 e é ignorado. Nada pra atualizar em cache: a tela já foi.
  useEffect(() => {
    const aoDescarregar = (): void => {
      podaPacotesSegurados();
      for (const id of pacotesSeguradosNestaAba.keys()) {
        soltarPacote(id, { keepalive: true }).catch(() => {});
      }
      pacotesSeguradosNestaAba.clear();
      seguradoRef.current = null;
    };
    window.addEventListener('pagehide', aoDescarregar);
    return () => window.removeEventListener('pagehide', aoDescarregar);
  }, []);

  // Estratégia de scroll consolidada (compensação de delta via ResizeObserver):
  //
  //  1. Troca de ticket (incl. F5+click): scroll instantâneo pro fim no useLayoutEffect.
  //  2. Mídia cresce APÓS render (imagem/áudio carregando): ResizeObserver detecta
  //     o `delta` de scrollHeight e, se user estava perto do fim, soma `delta` ao
  //     `scrollTop` — mantém a posição RELATIVA ao fim em vez de absoluta. Sem polling.
  //  3. Msg nova chegando: lógica idêntica ao item 2 (apenas delta compensation).
  //  4. Scroll-up (carrega bloco antigo): suprime compensação (`skipDeltaRef`),
  //     usa `prevScrollHeightRef` pra preservar posição visual no bloco que o user
  //     estava lendo.
  //  5. User longe do fim + msg nova: NÃO mexe no scroll, mostra "↓ Nova mensagem".
  const NEAR_BOTTOM_PX = 200;
  // Muda de false→true exatamente quando o dado do ticket chega e o spinner some,
  // permitindo que o useLayoutEffect de scroll re-execute mesmo que allMessages já
  // estivesse populado (race: mensagens chegaram antes do dado do ticket).
  const ticketLoaded = ticketQ.data != null;
  const prevScrollHeightRef = useRef<number | null>(null);
  // Baseline de scrollHeight do ResizeObserver. Vive em ref pra que o
  // useLayoutEffect da restauração (scroll-up) consiga re-sincronizar antes
  // do RO entregar o callback — evitando que o crescimento do prepend seja
  // interpretado como "conteúdo novo no fim" e jogue o user de volta pro fim.
  const roBaselineRef = useRef<number>(0);
  const lastScrolledTicketRef = useRef<number | null>(null);
  const lastSeenLastIdRef = useRef<number | null>(null);
  // Fila cega: rastreia a transição bloqueado→liberado pra re-scrollar pro fim.
  const prevContentHiddenRef = useRef(contentHidden);
  const scrollToBottomTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showNewMsgIndicator, setShowNewMsgIndicator] = useState(false);
  // Contador de msgs INBOUND (do cliente) que chegaram enquanto o user
  // está lendo histórico. Aparece como badge no indicador "Nova mensagem".
  // Reseta quando user volta near-bottom (manual OU click no indicador).
  const [newMsgCount, setNewMsgCount] = useState(0);

  /**
   * Busca na conversa — leva até uma mensagem achada. Já na tela: só rola.
   * Fora (mais antiga que o carregado): marca o alvo e pede o próximo bloco,
   * que o servidor faz descer de uma vez até ela (`ate`) — um pedido só em vez
   * de centenas de blocos de 20. A posição de leitura é preservada pelo mesmo
   * mecanismo da rolagem pra cima (`prevScrollHeightRef`).
   */
  const irParaResultado = useCallback(
    async (r: ResultadoDaBusca): Promise<void> => {
      const seq = ++seqDoSaltoRef.current;
      const root = scrollRef.current;
      if (!root || ticketId === null) return;
      const acha = (): HTMLElement | null =>
        root.querySelector<HTMLElement>(`[data-message-id="${r.id}"]`);

      let alvo = acha();
      if (!alvo) {
        marcarAlvoDaBusca(ticketId, r.created_at_micros);
        setDescendo(true);
        try {
          // Normalmente 1 volta basta; mais só se o alvo for mais fundo que o
          // teto de um bloco do servidor.
          let hasMore = messagesQ.hasNextPage ?? false;
          for (let volta = 0; volta < 5 && !alvo && hasMore; volta++) {
            if (scrollToBottomTimerRef.current !== null) {
              clearTimeout(scrollToBottomTimerRef.current);
              scrollToBottomTimerRef.current = null;
            }
            prevScrollHeightRef.current = scrollRef.current?.scrollHeight ?? null;
            const res = await messagesQ.fetchNextPage();
            if (seq !== seqDoSaltoRef.current) return;
            // Dois quadros pro React desenhar as bolhas novas antes de procurar.
            await new Promise<void>((ok) =>
              requestAnimationFrame(() => requestAnimationFrame(() => ok())),
            );
            if (seq !== seqDoSaltoRef.current) return;
            alvo = acha();
            hasMore = res.hasNextPage ?? false;
          }
        } finally {
          if (seq === seqDoSaltoRef.current) setDescendo(false);
        }
      }
      if (seq !== seqDoSaltoRef.current) return;
      if (!alvo) {
        toast.info('Mensagem não encontrada na conversa');
        return;
      }
      setFocoDaBusca(r.id);
      // Longe: pula direto (animar milhares de pixels só atrasa). Perto: suave.
      const caixa = root.getBoundingClientRect();
      const bolha = alvo.getBoundingClientRect();
      const distancia = Math.abs(bolha.top + bolha.height / 2 - (caixa.top + caixa.height / 2));
      alvo.scrollIntoView({
        block: 'center',
        behavior: distancia > root.clientHeight * 2 ? 'auto' : 'smooth',
      });
    },
    [ticketId, messagesQ],
  );

  const aoMudarTermoDaBusca = useCallback(
    (termo: string): void => {
      const seq = ++seqDaBuscaRef.current;
      seqDoSaltoRef.current += 1;
      setTermoDaBusca(termo);
      setFocoDaBusca(null);
      setIndiceDaBusca(0);
      setDescendo(false);
      if (!termo || ticketId === null) {
        setResultadosDaBusca(null);
        setBuscaTruncada(false);
        setBuscando(false);
        return;
      }
      setBuscando(true);
      buscarNaConversa(ticketId, termo)
        .then((r) => {
          if (seq !== seqDaBuscaRef.current) return;
          setResultadosDaBusca(r.results);
          setBuscaTruncada(r.truncated);
          setBuscando(false);
          // Já começa no mais recente.
          if (r.results[0]) void irParaResultado(r.results[0]);
        })
        .catch(() => {
          if (seq !== seqDaBuscaRef.current) return;
          setResultadosDaBusca(null);
          setBuscando(false);
          toast.error('Não foi possível buscar na conversa');
        });
    },
    [ticketId, irParaResultado],
  );

  /** +1 = mais antiga (↑), −1 = mais recente (↓). */
  const navegarNaBusca = (passo: 1 | -1): void => {
    if (!resultadosDaBusca) return;
    const novo = indiceDaBusca + passo;
    const r = resultadosDaBusca[novo];
    if (!r) return;
    setIndiceDaBusca(novo);
    void irParaResultado(r);
  };
  // Botão "↓" canto inferior direito — aparece sempre que o user scrolled up
  // (independente de chegar msg nova). Atalho pra voltar ao fim sem rolar manual.
  const [showScrollDownBtn, setShowScrollDownBtn] = useState(false);

  // Drag-and-drop + paste de arquivo no panel inteiro. Hooks ficam no TOPO
  // (antes dos early returns de loading/error) pra estabilidade da hooks-rule.
  // `composerDisabledRef` é atualizado mais abaixo com o valor real após
  // ticket carregar — listeners leem em fire time sem re-registrar.
  const composerRef = useRef<ComposerHandle>(null);
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);
  const composerDisabledRef = useRef(true); // default disabled até ticket carregar

  // Foca o textarea ao trocar de ticket OU quando status vira 'active'
  // (paridade WhatsApp Web). Cenários:
  // - User clica num card → cursor vai direto pro composer
  // - User aceita ticket pendente → status muda pending → active, composer
  //   sai do disabled, foca automaticamente (textarea[disabled] ignora
  //   .focus() do browser, então precisa esperar status mudar)
  // - User reabre ticket fechado → status muda closed → pending, NÃO foca
  //   (ainda disabled — aceitar depois dispara foco)
  // Composer expõe `focusInput()` via ref (RAF interno cobre re-render).
  useEffect(() => {
    if (ticketId === null) return;
    if (ticketQ.data?.status === 'active') {
      composerRef.current?.focusInput();
    }
  }, [ticketId, ticketQ.data?.status]);

  // Bloqueia default do browser na janela inteira pra drags com arquivo.
  // Sem isso, drop perto da borda abre o arquivo na aba e perde a sessão.
  useEffect(() => {
    const block = (e: DragEvent): void => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', block);
    window.addEventListener('drop', block);
    return () => {
      window.removeEventListener('dragover', block);
      window.removeEventListener('drop', block);
    };
  }, []);

  // Paste em nível de window — Cmd+V de print de tela em qualquer lugar do
  // panel (não precisa textarea focado). `defaultPrevented` evita double-upload
  // quando o textarea.onPaste já tratou.
  useEffect(() => {
    const onPasteWindow = (e: ClipboardEvent): void => {
      if (composerDisabledRef.current || e.defaultPrevented) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      // Todos os arquivos colados, não só o primeiro (o Composer aplica o teto).
      const files = Array.from(items)
        .filter((it) => it.kind === 'file')
        .map((it) => it.getAsFile())
        .filter((f): f is File => f !== null);
      if (files.length > 0) {
        e.preventDefault();
        void composerRef.current?.uploadFiles(files);
      }
    };
    window.addEventListener('paste', onPasteWindow);
    return () => window.removeEventListener('paste', onPasteWindow);
  }, []);

  const isNearBottom = (el: HTMLDivElement, fudge = 0): boolean =>
    el.scrollHeight - el.scrollTop - el.clientHeight - fudge < NEAR_BOTTOM_PX;

  const scrollToBottom = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    setShowNewMsgIndicator(false);
    setShowScrollDownBtn(false);
    setNewMsgCount(0);
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = isNearBottom(el);
    // Esconde indicador + zera contador quando user volta perto do fim manualmente.
    if (showNewMsgIndicator && nearBottom) {
      setShowNewMsgIndicator(false);
      setNewMsgCount(0);
    }
    // Botão "↓" só visível quando há histórico acima do viewport.
    setShowScrollDownBtn(!nearBottom);
    // Carrega bloco antigo ao chegar no topo.
    if (el.scrollTop < 120 && messagesQ.hasNextPage && !messagesQ.isFetchingNextPage) {
      // Cancela o timer de scroll-to-bottom do ticket-changed para não brigar
      // com a preservação de posição do fetchNextPage.
      if (scrollToBottomTimerRef.current !== null) {
        clearTimeout(scrollToBottomTimerRef.current);
        scrollToBottomTimerRef.current = null;
      }
      prevScrollHeightRef.current = el.scrollHeight;
      void messagesQ.fetchNextPage();
    }
  };

  // Reseta refs de scroll quando ticketId vira null (após transfer/close
  // que chama onTicketClosed). Sem isso, voltar a abrir o MESMO ticket
  // depois mantém `lastScrolledTicketRef === ticketId` → `ticketChanged`
  // fica false → useLayoutEffect abaixo não scrolla pro fim e a thread
  // abre congelada na posição anterior.
  useEffect(() => {
    if (ticketId === null) {
      lastScrolledTicketRef.current = null;
      lastSeenLastIdRef.current = null;
    }
  }, [ticketId]);

  // Marca ticket como lido no backend ao abrir — zera badge unread E limpa
  // transferred_at (some o ícone ↔ pra todos do dept via realtime). Backend
  // é idempotente; chamar sem necessidade não causa efeito colateral.
  //
  // E a resposta traz de carona os status das últimas mensagens de saída,
  // aplicados no cache: é o acerto de contas dos ✓/✓✓ que se perderam
  // enquanto a conversa esteve fechada (avisos de status só vão pra sala da
  // conversa). Zero requisição nova — vide `aplica-status-do-read.ts`.
  useEffect(() => {
    if (ticketId === null) return;
    void markTicketRead(ticketId)
      .then((r) => {
        aplicaStatusDoRead(qc, ticketId, r.statuses);
      })
      .catch(() => {
        // Best-effort — se falhar, próxima inbound dispara o read pelo
        // listener de realtime.
      });
  }, [ticketId, qc]);

  // Preservação de posição quando bloco antigo é carregado via scroll-up.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || prevScrollHeightRef.current === null) return;
    el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = null;
    // Re-sincroniza o baseline do ResizeObserver pro novo scrollHeight.
    // Sem isso, a próxima entrega assíncrona do RO veria delta = (novo - antigo)
    // e, se a página antiga era curta o suficiente pro user parecer "near bottom"
    // após o restore, somaria esse delta no scrollTop e jogaria ele de volta pro fim.
    roBaselineRef.current = el.scrollHeight;
  }, [messagesQ.data?.pages.length]);

  // Fila cega: quando o admin DESBLOQUEIA (contentHidden true→false), a thread
  // acabou de sair do placeholder e monta no topo. Zera lastScrolledTicketRef pra
  // o effect de scroll abaixo tratar como abertura do ticket (vai pro fim). Roda
  // ANTES do effect de scroll (declarado antes + ambos useLayoutEffect).
  // biome-ignore lint/correctness/useExhaustiveDependencies: só reage a contentHidden
  useLayoutEffect(() => {
    if (prevContentHiddenRef.current && !contentHidden) {
      lastScrolledTicketRef.current = null;
    }
    prevContentHiddenRef.current = contentHidden;
  }, [contentHidden]);

  // Scroll inicial ao trocar de ticket OU detectar msg nova com user perto do fim.
  // Sem polling — o ResizeObserver abaixo cuida do crescimento posterior do conteúdo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps intencionais
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || allMessages.length === 0) return;

    const lastMsgId = allMessages.at(-1)?.id ?? null;
    const ticketChanged = lastScrolledTicketRef.current !== ticketId;
    const newMessageArrived = !ticketChanged && lastMsgId !== lastSeenLastIdRef.current;

    if (ticketChanged) {
      lastScrolledTicketRef.current = ticketId;
      lastSeenLastIdRef.current = lastMsgId;
      setShowNewMsgIndicator(false);
      // Scroll instantâneo pro fim. Re-aplicado via rAF e timeout pra vencer
      // a restauração automática de scroll do Chrome após F5 (que pode
      // sobrescrever o scrollTop logo depois do useLayoutEffect).
      el.scrollTop = el.scrollHeight;
      requestAnimationFrame(() => {
        const e = scrollRef.current;
        if (e) e.scrollTop = e.scrollHeight;
      });
      scrollToBottomTimerRef.current = setTimeout(() => {
        scrollToBottomTimerRef.current = null;
        const e = scrollRef.current;
        if (e) e.scrollTop = e.scrollHeight;
      }, 100);
      return;
    }

    if (newMessageArrived) {
      lastSeenLastIdRef.current = lastMsgId;
      const lastMsg = allMessages.at(-1);
      const isOutbound = lastMsg?.direction === 'outbound';

      // Outbound = atendente acabou de enviar. SEMPRE puxa pro fim, mesmo se
      // estava lendo histórico — UX esperada: mandei msg, quero ver ela na tela.
      // Inbound (cliente): só puxa se já está near bottom; senão preserva
      // a posição do histórico e mostra indicador "↓ Nova mensagem (N)".
      //
      // "Perto do fim" é medido como estava ANTES da mensagem chegar. Aqui a
      // bolha nova já está no DOM, e a distância crua inclui a altura dela:
      // foto/vídeo nascem com o tamanho final e passam dos 200px sozinhos —
      // caíam no "Nova mensagem" com o atendente parado no fim. Desconta o que
      // cresceu desde a última medida do ResizeObserver (que ainda não viu a
      // bolha nova); é a mesma conta que ele faz pra mídia que cresce depois.
      // Não dá pra contar com ele aqui: a mensagem do cliente atualiza o
      // cabeçalho no mesmo instante, o que religa o observador já com a bolha
      // na medida — e a compensação dele se perde.
      const cresceu = Math.max(0, el.scrollHeight - roBaselineRef.current);
      if (isOutbound || isNearBottom(el, cresceu)) {
        el.scrollTop = el.scrollHeight;
        setShowNewMsgIndicator(false);
        setNewMsgCount(0);
        // Re-apply em 2 ticks pra cobrir crescimento pós-commit da bolha.
        // Caso típico: bolha com quote (reply_to) ou com mídia que faz
        // re-layout depois do useLayoutEffect — sem isso a bolha fica
        // parcial abaixo da viewport e user precisa scrollar manualmente.
        requestAnimationFrame(() => {
          const e = scrollRef.current;
          if (e) e.scrollTop = e.scrollHeight;
        });
        setTimeout(() => {
          const e = scrollRef.current;
          if (e) e.scrollTop = e.scrollHeight;
        }, 80);
      } else {
        setShowNewMsgIndicator(true);
        setNewMsgCount((c) => c + 1);
      }
    }
  }, [ticketId, allMessages.length, allMessages.at(-1)?.id, ticketLoaded, contentHidden]);

  // Delta compensation: quando o conteúdo cresce (mídia carregando ou bolha
  // expandindo), se user estava no fim, soma o delta ao scrollTop pra manter
  // posição relativa. O baseline `roBaselineRef` vive em ref pra que o
  // useLayoutEffect da restauração (scroll-up) re-sincronize antes da entrega
  // assíncrona do RO — assim o crescimento do prepend não é interpretado como
  // "conteúdo novo no fim".
  //
  // IMPORTANTE: o effect depende de `ticketId` E `ticketQ.data` porque o
  // scroll container só aparece no DOM depois que o ticket carregou (early
  // returns acima fazem scrollRef ficar null no primeiro mount). Sem essas
  // deps, o effect roda 1× com scrollRef=null e o observer nunca é registrado.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps intencionais
  useEffect(() => {
    const el = scrollRef.current;
    const wrapper = contentWrapperRef.current;
    if (!el || !wrapper) return;

    roBaselineRef.current = el.scrollHeight;
    let alturaVisivel = el.clientHeight;
    const ro = new ResizeObserver(() => {
      // A JANELA da conversa encolheu porque o composer cresceu (bandeja de
      // anexos, texto em várias linhas, gravação de áudio). O conteúdo não muda,
      // então o observer do wrapper não via nada e quem estava no fim ficava com
      // a última mensagem escondida atrás do composer. Estava no fim (medido com
      // a altura de antes) → continua no fim.
      const encolheu = alturaVisivel - el.clientHeight;
      if (encolheu > 0 && el.scrollHeight - el.scrollTop - alturaVisivel < NEAR_BOTTOM_PX) {
        el.scrollTop += encolheu;
      }
      alturaVisivel = el.clientHeight;

      const newH = el.scrollHeight;
      const delta = newH - roBaselineRef.current;
      roBaselineRef.current = newH;
      if (delta <= 0) return;
      if (isNearBottom(el, delta)) {
        el.scrollTop += delta;
      }
    });
    ro.observe(wrapper);
    // A própria área de rolagem também: é ela que encolhe quando o composer cresce.
    ro.observe(el);
    return () => ro.disconnect();
  }, [ticketId, ticketQ.data]);

  // Os eventos de sistema vêm INTEIROS na 1ª página, enquanto as mensagens
  // vêm em blocos — misturados por horário, os banners antigos se amontoavam
  // acima do bloco carregado e a conversa parecia não ter mensagem nenhuma.
  // O piso segura cada banner até o pedaço de conversa dele estar na tela
  // (vide eventos-do-bloco-carregado.ts).
  const pisoDeBanner = useMemo(
    () =>
      pisoDosBanners(
        allMessages.map((m) => m.created_at),
        messagesQ.hasNextPage ?? false,
      ),
    [allMessages, messagesQ.hasNextPage],
  );

  // Transfer events só vêm na 1ª página da query. Frontend interleva
  // cronologicamente com as msgs e renderiza banner inline.
  // Filtra transferências disparadas pelo chatbot (actor_user_id IS NULL =
  // sistema/automação) — no /atendimento o atendente só quer ver transfer
  // feita por colega humano, não a interna da automação. Exceção: a devolução
  // de quem ficou offline (vigia) — é o que explica o ticket ter voltado.
  const transferEvents = useMemo(
    () =>
      dentroDoBlocoCarregado(
        (messagesQ.data?.pages[0]?.transfer_events ?? []).filter(
          (t) => t.actor_user_id !== null || t.motivo === 'atendente_offline',
        ),
        (e) => e.created_at,
        pisoDeBanner,
      ),
    [messagesQ.data, pisoDeBanner],
  );

  // Reaberturas (botão Reabrir — sempre ação humana) — banner inline igual
  // ao de transferência.
  const reopenEvents = useMemo(
    () =>
      dentroDoBlocoCarregado(
        messagesQ.data?.pages[0]?.reopen_events ?? [],
        (e) => e.created_at,
        pisoDeBanner,
      ),
    [messagesQ.data, pisoDeBanner],
  );

  /**
   * Pacote preso no fim solta SOZINHO no instante do prazo (relógio do
   * servidor) — mesmo sem o aviso de fechamento — e passa a ordenar pelo
   * prazo. Como isso é função do relógio e não de dado novo, o memo abaixo não
   * re-rodaria por conta própria: `tique` é bumpado por um timer agendado pro
   * instante exato do próximo desprendimento.
   */
  const [tique, setTique] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `tique` só existe pra re-rodar pelo relógio
  const grouped = useMemo(
    () =>
      groupItemsByDay(
        buildThreadItems(allMessages, transferEvents, reopenEvents, agoraDoServidor()),
      ),
    [allMessages, transferEvents, reopenEvents, tique],
  );
  // Re-arma depois de cada tique: pode haver mais de um preso, cada um com seu prazo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `tique` re-arma o timer
  useEffect(() => {
    const agora = agoraDoServidor();
    const proximo = proximoDesprendimento(allMessages, (m) => m, agora);
    if (proximo === null) return;
    const t = setTimeout(() => setTique((n) => n + 1), Math.max(0, proximo - agora) + 50);
    return () => clearTimeout(t);
  }, [allMessages, tique]);

  if (ticketId === null) {
    return (
      <section className="flex flex-1 flex-col items-center justify-center bg-n-50 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-pill bg-brand-50 text-brand-600">
          <MessageSquare size={28} strokeWidth={1.5} />
        </div>
        <h2 className="mt-3 font-semibold text-[15px] text-n-700">Selecione um atendimento</h2>
        <p className="mt-1 max-w-xs text-[13px] text-n-500">
          Clique em um card da lista pra abrir a conversa.
        </p>
      </section>
    );
  }

  if (ticketQ.isError) {
    // Atendimento em aberto de outro agente (sem "Visualizar todos"): o
    // servidor recusa de propósito — não é erro, é aviso.
    const comOutroAgente =
      ticketQ.error instanceof ApiError && ticketQ.error.code === 'TICKET_WITH_OTHER_AGENT';
    return (
      <section className="flex flex-1 flex-col items-center justify-center gap-3 bg-n-50">
        <p className="text-[13px] text-n-500">
          {comOutroAgente
            ? 'Este atendimento está com outro agente.'
            : 'Erro ao carregar atendimento.'}
        </p>
        {onVoltar && (
          <button
            type="button"
            onClick={onVoltar}
            className="text-[12px] font-semibold text-brand-600 hover:underline"
          >
            Voltar para a lista
          </button>
        )}
        {!comOutroAgente && (
          <button
            type="button"
            onClick={() => {
              void ticketQ.refetch();
              void messagesQ.refetch();
            }}
            className="rounded-md bg-brand-500 px-4 py-2 text-[12px] font-semibold text-white hover:bg-brand-600"
          >
            Tentar novamente
          </button>
        )}
      </section>
    );
  }

  if (ticketQ.isLoading || !ticketQ.data) {
    return (
      <section className="flex flex-1 items-center justify-center bg-n-50">
        <Loader2 size={20} className="animate-spin text-n-400" />
      </section>
    );
  }

  const ticket = ticketQ.data;
  const isClosed = ticket.status === 'closed';
  const isPending = ticket.status === 'pending';
  // Atendimento de COLEGA aberto via "Visualizar todos" (perfil de acesso):
  // só visualização. Responder/fila/transferir é do dono ou do admin (o
  // servidor já recusa); fechar/reabrir/ligar também ficam fora pra quem só vê.
  // Só decide com a sessão carregada — sem isso, a própria conversa aparecia
  // como "só visualização" por um instante no boot.
  const soVisualizacao =
    userId != null &&
    userProfile != null &&
    ticket.user_id !== null &&
    ticket.user_id !== userId &&
    userProfile !== 'admin' &&
    !isSupport;
  // Canal qr_code não tem janela de 24h — texto livre sempre permitido.
  const within24h =
    ticket.whatsapp_number.channel_type === 'qr_code' ||
    (ticket.contact.last_inbound_at &&
      Date.now() - new Date(ticket.contact.last_inbound_at).getTime() <= TWENTY_FOUR_HOURS_MS);
  // Janela 24h fechada (só Cloud API, ticket ativo): o composer NÃO trava por
  // completo — libera SÓ nota interna (não vai pro WhatsApp, então a regra da
  // Meta não se aplica). Envio real continua bloqueado (hint de template).
  const fullyDisabled = isClosed || isPending || soVisualizacao;
  const windowExpired = !fullyDisabled && !within24h;
  // Gate de ARQUIVOS (drag-drop + paste de print): janela fechada também
  // bloqueia — nota interna é só texto.
  const composerDisabled = fullyDisabled || !within24h;

  // Sincroniza ref com valor atual de composerDisabled — listeners globais
  // (registrados nos useEffects no topo do componente) leem isso em fire time
  // sem precisar re-registrar a cada mudança de status.
  composerDisabledRef.current = composerDisabled;

  const onDragEnter = (e: React.DragEvent<HTMLElement>): void => {
    if (composerDisabled) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setDragActive(true);
  };
  const onDragOver = (e: React.DragEvent<HTMLElement>): void => {
    if (composerDisabled) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = (e: React.DragEvent<HTMLElement>): void => {
    if (composerDisabled) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragActive(false);
    }
  };
  const onDrop = (e: React.DragEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragActive(false);
    if (composerDisabled) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      void composerRef.current?.uploadFiles(files);
    }
  };

  return (
    <section
      className="relative flex min-w-0 flex-1 flex-col bg-n-50"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Header */}
      <header
        className={`flex h-[64px] flex-shrink-0 items-center gap-3 border-n-200 border-b bg-n-0 ${
          onVoltar ? 'pr-5 pl-2' : 'px-5'
        }`}
      >
        {onVoltar && (
          <button
            type="button"
            onClick={onVoltar}
            aria-label="Voltar para a lista"
            title="Voltar para a lista"
            className="flex h-[36px] w-[36px] flex-shrink-0 items-center justify-center rounded-md text-n-600 hover:bg-n-100"
          >
            <ArrowLeft size={20} strokeWidth={1.75} />
          </button>
        )}
        {/* Container único clicável — foto + nome + tags. Não usa <button>
            porque o TagsPopover interno tem seu próprio botão (HTML proíbe
            nesting). Click em qualquer área abre o modal de info, EXCETO se
            o click acertou a região das tags (TagsPopover trata).

            stopPropagation NÃO funciona como wrapper "geral" porque engole
            clicks que caem em espaço vazio próximo ao +Tag — daí o user achava
            que "clicar 2x não abre". Aqui usamos ref check: clicks dentro do
            bounding box das tags são ignorados pelo parent; +Tag e tag pills
            preservam seus handlers próprios. */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard via onKeyDown abaixo */}
        <div
          // Fila cega: quando bloqueado, o cabeçalho NÃO abre o modal de info
          // (que mostra o número) — sem role/onClick/hover, fica inerte.
          {...(!contentHidden && {
            role: 'button',
            tabIndex: 0,
            onClick: (e: React.MouseEvent<HTMLDivElement>) => {
              const target = e.target as HTMLElement;
              // Só ignora se o click foi num botão dentro das tags (pill, X de
              // remover, +Tag ou item do popover). Espaço vazio na linha das
              // tags ainda abre o modal — fix do "clicar na linha da tag não
              // abre".
              if (tagsRegionRef.current?.contains(target) && target.closest('button')) {
                return;
              }
              setContactDetailsOpen(true);
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setContactDetailsOpen(true);
              }
            },
            'aria-label': 'Abrir informações do contato',
            title: 'Informações do contato',
          })}
          // Fundo acende só onde o clique abre as informações do contato: em
          // cima de um botão da linha das etiquetas (telefone, + Tag, X) o
          // clique é dele, e o cabeçalho fica parado — mesma regra do onClick.
          className={`-mx-2 flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1 ${
            contentHidden
              ? ''
              : 'cursor-pointer transition [&:hover:not(:has(button:hover))]:bg-n-50'
          }`}
        >
          <div
            className="relative flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center overflow-hidden rounded-pill bg-n-100 text-n-400"
            style={anelDoAvatar(ticket.whatsapp_number.channel_type === 'instagram')}
          >
            {ticket.contact.profile_pic_url ? (
              <img
                src={ticket.contact.profile_pic_url}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <User size={24} strokeWidth={1.5} />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-[4px]">
            <span className="truncate font-bold text-[15px] text-n-900 leading-tight">
              {ticket.contact.display_name ?? 'Sem nome'}
            </span>
            {/* Nome a 4px da 2ª linha (tag OU, na fila cega, a mensagem) —
                mesmo espaçamento nos dois casos. */}
            <div ref={tagsRegionRef}>
              {contentHidden ? (
                <span className="text-[11px] leading-tight text-n-500">
                  Aceite o atendimento para visualizar.
                </span>
              ) : (
                <div className="flex items-center gap-1.5">
                  {ticket.whatsapp_number.channel_type !== 'instagram' && (
                    <CopiarNumero telefone={ticket.contact.phone} />
                  )}
                  <TagsPopover
                    ticketId={ticket.id}
                    applied={ticket.tags}
                    readOnly={soVisualizacao}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-[4px]">
          {isClosed && !soVisualizacao && (
            <ActionButton
              icon={Undo2}
              label="Retornar para Fila"
              onClick={() => reopenMut.mutate()}
              loading={reopenMut.isPending}
            />
          )}
          {isPending && (
            <ActionButton
              icon={Check}
              label="Aceitar conversa"
              onClick={() => acceptMut.mutate()}
              loading={acceptMut.isPending}
            />
          )}
          {!isClosed && !isPending && !soVisualizacao && (
            <ActionButton
              icon={Undo2}
              label="Retornar para Fila"
              onClick={() => queueMut.mutate()}
              loading={queueMut.isPending}
            />
          )}
          {!isClosed && !soVisualizacao && (
            <>
              {voipEnabled &&
                ticket.whatsapp_number.provider === 'zapo' &&
                ticket.whatsapp_number.channel_type === 'qr_code' && (
                  <ActionButton
                    icon={Phone}
                    label="Ligar"
                    onClick={() => {
                      // Dupla confirmação — clique acidental liga pro cliente
                      // de verdade (aconteceu). O clique no "Ligar" do diálogo
                      // segue sendo gesto do usuário → o mic abre normal.
                      if (callSession.busy) {
                        toast.info('Já tem uma ligação em andamento', {
                          description: 'Encerre a ligação atual antes de iniciar outra.',
                        });
                        return;
                      }
                      void (async () => {
                        const ok = await confirm({
                          title: `Ligar para ${ticket.contact.display_name ?? 'o contato'}?`,
                          description:
                            'Uma chamada de voz do WhatsApp será iniciada para este contato.',
                          confirmText: 'Ligar',
                        });
                        if (!ok) return;
                        callSession.startOutbound(
                          ticket.id,
                          ticket.contact.display_name ?? 'Contato',
                        );
                      })();
                    }}
                  />
                )}
              <ActionButton
                icon={TransferPerson}
                label="Transferir"
                onClick={() => setTransferOpen(true)}
              />
            </>
          )}
          {/* Lupa logo antes do Fechar (em conversa fechada, por último). */}
          {!contentHidden && (
            <ActionButton
              icon={Search}
              label="Buscar na conversa"
              {...(buscaAberta && { iconClassName: 'bg-brand-50 text-brand-600' })}
              onClick={() => (buscaAberta ? fecharBusca() : setBuscaAberta(true))}
            />
          )}
          {!isClosed && !soVisualizacao && (
            <ActionButton
              icon={MessageCircleX}
              label="Fechar"
              iconClassName="text-danger-500"
              onClick={() => setCloseOpen(true)}
            />
          )}
        </div>
      </header>

      {/* Body — placeholder da fila cega (pendente + canal oculto + não-admin)
          OU a conversa. Header + composer continuam (pra poder Aceitar). */}
      {contentHidden ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-n-100 px-8 text-center">
          <Lock size={34} strokeWidth={1.5} className="text-n-400" />
          <p className="text-[15px] font-semibold text-n-700">Conteúdo oculto</p>
          <p className="max-w-xs text-[13px] text-n-500">
            Aceite o atendimento para visualizar a conversa.
          </p>
        </div>
      ) : (
        <div className="relative flex min-h-0 flex-1 flex-col">
          {buscaAberta && (
            <BuscaNaConversa
              indice={indiceDaBusca}
              total={resultadosDaBusca ? resultadosDaBusca.length : null}
              truncado={buscaTruncada}
              ocupado={buscando || descendo}
              onTermo={aoMudarTermoDaBusca}
              onMaisAntiga={() => navegarNaBusca(1)}
              onMaisRecente={() => navegarNaBusca(-1)}
              onFechar={fecharBusca}
            />
          )}
          {/* Fundo de rabiscos do app, tingido pelo tema — vide `.fundo-da-conversa`
              no globals.css (claro e escuro têm imagem e intensidade próprias). */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="fundo-da-conversa flex flex-1 flex-col overflow-y-auto px-7 py-5"
          >
            {/* Wrapper único do conteúdo. ResizeObserver observa essa div pra
              detectar QUALQUER crescimento interno (mídia carregando em qualquer
              bolha). Sem esse wrapper, ResizeObserver no firstElementChild
              capturava só o primeiro filho. */}
            <div ref={contentWrapperRef} className="flex flex-col gap-2.5">
              {messagesQ.isLoading && (
                <div className="flex flex-1 items-center justify-center">
                  <Loader2 size={20} className="animate-spin text-n-400" />
                </div>
              )}
              {messagesQ.isFetchingNextPage && (
                <div className="flex justify-center py-2">
                  <Loader2 size={16} className="animate-spin text-n-400" />
                </div>
              )}
              {!messagesQ.hasNextPage && !messagesQ.isLoading && allMessages.length > 0 && (
                <div className="py-2 text-center text-[11px] text-n-400">Início da conversa</div>
              )}
              {grouped.map((group, gi) => (
                <div key={`group-${gi}-${group.label}`} className="flex flex-col gap-2.5">
                  <DayDivider label={group.label} />
                  {group.items.map((item) => {
                    if (item.kind === 'event') {
                      return <TransferBanner key={`e-${item.data.id}`} event={item.data} />;
                    }
                    if (item.kind === 'reopen') {
                      return <ReopenBanner key={`r-${item.data.id}`} event={item.data} />;
                    }
                    const m = item.data;
                    // Mensagens de sistema (ex: registro de chamada de voz)
                    // renderizam como banner central, estilo transferência.
                    if (m.type === 'system') {
                      return <SystemEventBanner key={`m-${m.id}`} message={m} />;
                    }
                    // Só habilita Excluir em outbound + canal qr_code (Cloud API
                    // não suporta delete-for-both). MessageBubble já checa wamid
                    // e !deleted_at internamente.
                    const canDelete =
                      m.direction === 'outbound' &&
                      ticket.whatsapp_number.channel_type === 'qr_code';
                    // Editar: tipo/canal/autor aqui; o prazo de 15min a bolha
                    // checa no hover (muda com o tempo).
                    const canEdit = podeEditarMensagem(m, {
                      userId,
                      podeEditarDeOutros: userProfile === 'admin' || isSupport,
                      canalQr: ticket.whatsapp_number.channel_type === 'qr_code',
                      canalPermite: ticket.whatsapp_number.allow_message_edit ?? false,
                    });
                    return (
                      <MessageBubble
                        key={`m-${m.id}`}
                        message={m}
                        // Conversa de colega aberta só pra ver: sem responder,
                        // enviar/descartar pacote, excluir ou editar.
                        {...(!soVisualizacao && {
                          onReply: (msg: ChatMessage) => {
                            setEditingMessage(null);
                            setReplyingTo(msg);
                            // Clicou em Responder → já pode digitar, sem levar o
                            // mouse até o campo (igual ao Editar; dono, 19/out/2026).
                            composerRef.current?.focusInput();
                          },
                          onEnviarAgora: handleEnviarAgora,
                          onDescartar: handleDescartar,
                        })}
                        onJumpToQuoted={handleJumpToQuoted}
                        {...(canDelete && !soVisualizacao && { onDelete: handleDeleteMessage })}
                        {...(canEdit && !soVisualizacao && { onEdit: handleEditMessage })}
                        {...(buscaAberta && termoDaBusca && { termoDaBusca })}
                        emFoco={focoDaBusca === m.id}
                      />
                    );
                  })}
                </div>
              ))}
              {/* Sentinela invisível pro auto-scroll. */}
              <div ref={endSentinelRef} aria-hidden="true" className="h-px w-px" />
            </div>
          </div>

          {/* Indicador "↓ Nova mensagem" — aparece quando user está lendo histórico
            e chegou msg do cliente. Badge mostra quantas inbounds acumularam.
            Click puxa pro fim e zera contador. */}
          {showNewMsgIndicator && (
            <button
              type="button"
              onClick={scrollToBottom}
              className="absolute bottom-4 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-pill bg-brand-500 px-3.5 py-1.5 font-semibold text-[12px] text-white shadow-md hover:bg-brand-600"
            >
              <ArrowDown size={14} strokeWidth={2.5} />
              {newMsgCount > 1 ? `${newMsgCount} novas mensagens` : 'Nova mensagem'}
            </button>
          )}

          {/* Botão circular "↓" canto inferior direito — atalho pra rolar pro fim
            quando o atendente está lendo histórico. Padrão WhatsApp/Telegram. */}
          {showScrollDownBtn && (
            <button
              type="button"
              onClick={scrollToBottom}
              aria-label="Rolar pra baixo"
              title="Rolar pra baixo"
              className="absolute bottom-4 right-[26px] flex h-10 w-10 items-center justify-center rounded-pill bg-brand-100 text-brand-700 shadow-md transition hover:bg-brand-200"
            >
              <ChevronDown size={20} strokeWidth={2} />
            </button>
          )}
        </div>
      )}

      {/* Composer — bloqueado enquanto ticket pending: precisa aceitar primeiro.
          key={ticket.id} força remount ao trocar de chat: reseta value, height
          auto-resize, pending attachment, recording, emoji picker. Sem isso o
          rascunho digitado em A ficava visível em B + altura expandida não
          encolhia. */}
      <Composer
        key={ticket.id}
        ref={composerRef}
        disabled={fullyDisabled}
        {...(soVisualizacao && {
          disabledMessage: 'Atendimento de outro atendente — só visualização',
        })}
        noteOnly={windowExpired}
        isPending={isPending}
        contactDisplayName={ticket.contact.display_name}
        contactPhone={ticket.contact.phone}
        contactEmail={ticket.contact.email}
        contactStateUf={ticket.contact.state_uf}
        contactCustomFields={ticket.contact.custom_fields}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
        editing={editingMessage}
        onCancelEdit={() => setEditingMessage(null)}
        onSaveEdit={handleSaveEdit}
        onTextPresenceChange={onTextPresenceChange}
        draftKey={ticket.id}
        onSend={async (text) => {
          await sendMut.mutateAsync({
            body: text,
            replyToWamid: replyingTo?.wamid ?? undefined,
          });
        }}
        onSendMedia={async (input) => {
          await sendMediaMut.mutateAsync({
            type: input.type,
            media_id: input.media_id,
            ...(input.caption && { caption: input.caption }),
            replyToWamid: replyingTo?.wamid ?? undefined,
            ...(input.width && input.height && { width: input.width, height: input.height }),
            ...(input.poster && { poster: input.poster }),
          });
        }}
        onSendNote={async (text) => {
          await sendNoteMut.mutateAsync(text);
        }}
        // Agendar mensagem: só canal QR Code (na API oficial o texto livre
        // depende da janela de 24h, que pode ter fechado na hora do envio).
        {...(ticket.whatsapp_number.channel_type === 'qr_code' && {
          onSchedule: () => setAgendarAberto(true),
        })}
        // `onVoltar` só existe com a lista escondida (janela estreita).
        menuCompacto={onVoltar !== undefined}
        // Instagram não aceita documento.
        semDocumento={ticket.whatsapp_number.channel_type === 'instagram'}
      />

      <ScheduleMessageModal
        contexto={
          agendarAberto
            ? {
                tipo: 'novo',
                ticketId: ticket.id,
                contato: {
                  displayName: ticket.contact.display_name,
                  phone: ticket.contact.phone,
                  email: ticket.contact.email,
                  stateUf: ticket.contact.state_uf,
                  customFields: ticket.contact.custom_fields,
                },
              }
            : null
        }
        onClose={() => setAgendarAberto(false)}
      />

      {/* Canal qr_code (whatsmeow) não tem janela 24h — texto livre sempre permitido.
          Countdown da janela aparece no CARD do ticket (TicketCard.tsx), não aqui. */}
      {!within24h &&
        !isClosed &&
        !soVisualizacao &&
        ticket.whatsapp_number.channel_type !== 'qr_code' && (
          <Window24hHint
            lastInboundAt={ticket.contact.last_inbound_at}
            whatsappNumberId={ticket.whatsapp_number.id}
            ticketId={ticket.id}
            instagram={ticket.whatsapp_number.channel_type === 'instagram'}
          />
        )}

      <CloseModal
        open={closeOpen}
        exigeMotivo={ticket.whatsapp_number.require_closing_reason ?? true}
        onClose={() => setCloseOpen(false)}
        onConfirm={async (reason, notes) => {
          try {
            await closeMut.mutateAsync({ reason, ...(notes && { notes }) });
          } catch (err) {
            // 409 = o atendimento mudou (já fechado / com outro): o aviso já
            // saiu pelo onError e não há o que tentar de novo — deixa o modal
            // fechar. Outro erro mantém o modal aberto com a escolha feita.
            if (!(err instanceof ApiError && err.status === 409)) throw err;
          }
        }}
      />

      <TransferModal
        ticketId={ticket.id}
        currentUserId={ticket.user_id}
        currentDepartmentId={ticket.department_id}
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onTransferred={() => {
          setTransferOpen(false);
          // Conversa some — foi pra outro atendente/depto.
          onTicketClosed?.();
        }}
      />

      <ContactDetailsModal
        contactId={ticket.contact.id}
        profilePicUrl={ticket.contact.profile_pic_url}
        // Fila cega: nunca abre o modal de info (mostra o número) enquanto bloqueado.
        open={contactDetailsOpen && !contentHidden}
        onClose={() => setContactDetailsOpen(false)}
      />

      {dragActive && !composerDisabled && (
        <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-brand-500/10 backdrop-blur-[1px]">
          <div className="flex flex-col items-center gap-3 rounded-lg border-2 border-brand-500 border-dashed bg-white/95 px-10 py-8 shadow-lg">
            <Upload size={36} strokeWidth={1.75} className="text-brand-500" />
            <div className="text-center">
              <p className="font-semibold text-[15px] text-n-900">Solte para anexar</p>
              <p className="mt-1 text-[12px] text-n-500">
                Imagem, vídeo, áudio ou documento — até {MAX_ANEXOS} de uma vez
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Window24hHint({
  lastInboundAt,
  whatsappNumberId,
  ticketId,
  instagram = false,
}: {
  lastInboundAt: string | null;
  whatsappNumberId: number;
  ticketId: number;
  /** Instagram não tem template: fora da janela, só esperar o cliente. */
  instagram?: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  if (instagram) {
    return (
      <div className="border-warning-500 border-t-2 bg-warning-50 px-5 py-2.5 text-[12px] text-warning-700">
        Janela de 24h do Instagram expirou. Só dá pra responder quando o cliente mandar uma nova
        mensagem.
      </div>
    );
  }
  return (
    <>
      <div className="flex items-center justify-between gap-3 border-warning-500 border-t-2 bg-warning-50 px-5 py-2.5 text-[12px] text-warning-700">
        <span>
          Janela de 24h expirou. Envie um template aprovado e aguarde o cliente responder para
          continuar a conversa.
        </span>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="rounded-md bg-warning-500 px-3 py-1.5 font-semibold text-[11px] text-white hover:bg-warning-600"
        >
          Escolher template
        </button>
      </div>
      {pickerOpen && (
        <TemplatePickerModal
          onClose={() => setPickerOpen(false)}
          whatsappNumberId={whatsappNumberId}
          ticketId={ticketId}
        />
      )}
    </>
  );
}

// Botões de ação do topo: só o ícone (18×18), cinza, hover com fundo
// cinza-claro. Tooltip PRÓPRIO (não o `title` nativo, que tem delay do browser
// não-configurável) — aparece instantâneo no hover. `label`/`title` = texto do
// tooltip + aria-label.
function ActionButton({
  icon: Icon,
  label,
  onClick,
  disabled,
  loading,
  title,
  iconClassName,
}: {
  icon: typeof Video;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  title?: string;
  /** Cor do ícone (Tailwind text-*). Default cinza (text-n-600). */
  iconClassName?: string;
}) {
  const tip = title ?? label;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={label}
      title={tip}
      className={`inline-flex h-[34px] w-[34px] items-center justify-center rounded-md transition-colors hover:bg-n-100 disabled:cursor-not-allowed disabled:opacity-50 ${iconClassName ?? 'text-n-600'}`}
    >
      {loading ? (
        <Loader2 size={18} className="animate-spin" />
      ) : (
        <Icon size={18} strokeWidth={2} />
      )}
    </button>
  );
}

function DayDivider({ label }: { label: string }) {
  return (
    <div className="my-2 flex w-full items-center gap-3 font-semibold text-[11px] text-n-400 before:h-px before:flex-1 before:bg-n-200 before:content-[''] after:h-px after:flex-1 after:bg-n-200 after:content-['']">
      <span className="whitespace-nowrap rounded-pill border border-n-200 bg-n-0 px-3.5 py-1">
        {label}
      </span>
    </div>
  );
}

interface DayGroup {
  label: string;
  messages: ChatMessage[];
}

function groupByDay(messages: ChatMessage[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const m of messages) {
    const label = dayLabel(m.created_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.messages.push(m);
    } else {
      groups.push({ label, messages: [m] });
    }
  }
  return groups;
}

/** Item da thread — mensagem ou evento (transferência/reabertura) inline. */
type ThreadItem =
  | { kind: 'message'; ts: string; data: ChatMessage }
  | { kind: 'event'; ts: string; data: TransferEvent }
  | { kind: 'reopen'; ts: string; data: ReopenEvent };

interface ItemDayGroup {
  label: string;
  items: ThreadItem[];
}

/**
 * Mescla messages + eventos de sistema em uma lista ordenada por timestamp.
 * Pacote de texto ainda aberto (pelo relógio do servidor) vai pro FIM, seja
 * qual for o carimbo (vide pacote-aberto-no-fim.ts): o que o cliente mandar
 * enquanto o relógio corre entra acima, como no WhatsApp dele. Vencido o
 * prazo, ordena pelo prazo. O `ts` do preso vira o do último item de verdade
 * pra ele não abrir um divisor de dia próprio (pacote aberto na virada da
 * meia-noite).
 */
function buildThreadItems(
  messages: ChatMessage[],
  events: TransferEvent[],
  reopens: ReopenEvent[] = [],
  agoraMs: number = Date.now(),
): ThreadItem[] {
  const items: ThreadItem[] = [];
  // Pacote cujo prazo já venceu ordena pelo PRAZO (= hora do envio, relógio do
  // servidor), mesmo que o aviso de fechamento ainda não tenha trazido o
  // carimbo exato — vide pacote-aberto-no-fim.ts.
  for (const m of messages)
    items.push({ kind: 'message', ts: carimboDeOrdenacao(m, agoraMs), data: m });
  for (const e of events) items.push({ kind: 'event', ts: e.created_at, data: e });
  for (const r of reopens) items.push({ kind: 'reopen', ts: r.created_at, data: r });
  items.sort((a, b) => a.ts.localeCompare(b.ts));
  const mensagem = (it: ThreadItem): ChatMessage | null => (it.kind === 'message' ? it.data : null);
  const ordenados = comPacotesAbertosNoFim(items, mensagem, agoraMs);
  const preso = (it: ThreadItem): boolean => {
    const m = mensagem(it);
    return m !== null && pacoteAberto(m, agoraMs);
  };
  const ultimoNaoPreso = [...ordenados].reverse().find((it) => !preso(it));
  if (!ultimoNaoPreso) return ordenados;
  return ordenados.map((it) =>
    preso(it) && it.ts < ultimoNaoPreso.ts ? { ...it, ts: ultimoNaoPreso.ts } : it,
  );
}

function groupItemsByDay(items: ThreadItem[]): ItemDayGroup[] {
  const groups: ItemDayGroup[] = [];
  for (const item of items) {
    const label = dayLabel(item.ts);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  }
  return groups;
}

/** Banner central pra mensagens de sistema (registro de chamada de voz etc).
 *  Mesmo visual do TransferBanner; o body já vem como texto final. */
/** Ícone do banner conforme a ação da chamada (match no texto do body —
 *  o worker controla os textos). Perdida/recusada/não atendida em vermelho. */
function callBannerIcon(body: string): { Icon: typeof Phone; tint: string | null } {
  if (body.includes('ligando')) return { Icon: PhoneIncoming, tint: null };
  if (body.includes('perdida') || body.includes('não atendida')) {
    return { Icon: PhoneMissed, tint: 'text-danger-500' };
  }
  if (body.includes('recusada')) return { Icon: PhoneOff, tint: 'text-danger-500' };
  if (body.includes('recebida')) return { Icon: PhoneIncoming, tint: null };
  if (body.includes('realizada')) return { Icon: PhoneOutgoing, tint: null };
  if (body.includes('cancelada') || body.includes('ocupado')) return { Icon: PhoneOff, tint: null };
  // "atendida no celular" — o "não atendida" (vermelho) já retornou acima.
  if (body.includes('atendida')) return { Icon: PhoneIncoming, tint: null };
  return { Icon: Phone, tint: null };
}

function SystemEventBanner({ message }: { message: ChatMessage }) {
  const time = new Date(message.created_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  // strip do emoji legado — registros antigos tinham 📞 no texto
  const body = (message.body ?? '').replace(/^📞\s*/, '');
  const { Icon, tint } = callBannerIcon(body);
  return (
    <div className="my-1 flex justify-center px-4">
      <span className="inline-flex max-w-[90%] items-center gap-1.5 rounded-pill border border-brand-200 bg-brand-50 px-4 py-1.5 text-[12px] font-medium text-brand-700">
        <Icon size={13} {...(tint && { className: tint })} />
        <span className="truncate">{body}</span>
        <span className="font-normal text-brand-500">·</span>
        <span className="whitespace-nowrap font-normal text-brand-500">{time}</span>
      </span>
    </div>
  );
}

/** Banner inline mostrando uma transferência no histórico do ticket.
 *  Visual segue o padrão do DayDivider (pill centralizado leve), mas em tom
 *  brand pra diferenciar da divider de data. */
function TransferBanner({ event }: { event: TransferEvent }) {
  const time = new Date(event.created_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <div className="my-1 flex justify-center px-4">
      <span className="inline-flex max-w-[90%] items-center gap-1.5 rounded-pill border border-brand-200 bg-brand-50 px-4 py-1.5 text-[12px] font-medium text-brand-700">
        <ArrowRightLeft size={13} />
        <span className="truncate">{formatTransferText(event)}</span>
        <span className="font-normal text-brand-500">·</span>
        <span className="whitespace-nowrap font-normal text-brand-500">{time}</span>
      </span>
    </div>
  );
}

/** Banner inline de reabertura — mesmo visual do TransferBanner. */
function ReopenBanner({ event }: { event: ReopenEvent }) {
  const time = new Date(event.created_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const agendado = event.kind === 'scheduled_open';
  const quem = event.actor_user_name;
  const text = agendado
    ? quem
      ? `Atendimento aberto por agendamento de ${quem}`
      : 'Atendimento aberto por agendamento'
    : quem
      ? `Atendimento reaberto por ${quem}`
      : 'Atendimento reaberto';
  const Icone = agendado ? CalendarClock : Undo2;
  return (
    <div className="my-1 flex justify-center px-4">
      <span className="inline-flex max-w-[90%] items-center gap-1.5 rounded-pill border border-brand-200 bg-brand-50 px-4 py-1.5 text-[12px] font-medium text-brand-700">
        <Icone size={13} />
        <span className="truncate">{text}</span>
        <span className="font-normal text-brand-500">·</span>
        <span className="whitespace-nowrap font-normal text-brand-500">{time}</span>
      </span>
    </div>
  );
}

function formatTransferText(e: TransferEvent): string {
  if (e.motivo === 'atendente_offline') {
    const quem = e.from_user_name ?? 'Atendente';
    return e.to_user_name
      ? `${quem} ficou offline — atendimento transferido para ${e.to_user_name}`
      : `${quem} ficou offline — atendimento voltou para Pendentes`;
  }
  const from = e.from_user_name;
  const to = e.to_user_name;
  const fromDept = e.from_department_name;
  const toDept = e.to_department_name;

  if (to) {
    if (from && from !== to) return `Atendimento transferido de ${from} para ${to}`;
    if (from === to) return `Atendimento puxado por ${to}`;
    return `Atendimento transferido para ${to}`;
  }
  if (e.queued) {
    if (toDept) return `Atendimento enviado para a fila de ${toDept}`;
    return 'Atendimento enviado para a fila';
  }
  if (toDept) {
    if (fromDept && fromDept !== toDept) {
      return `Departamento alterado de ${fromDept} para ${toDept}`;
    }
    return `Departamento definido como ${toDept}`;
  }
  return 'Atendimento transferido';
}
